import type { ApolloError } from '@apollo/client';
import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Heading,
  Spacer,
  Stack,
  useToast,
} from '@hover/blueprint';
import type { GraphQLError } from 'graphql/index';
import type { ConsentArgs } from 'mock/generated/types';
import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router';
import type { InferType } from 'yup';

import { BackButton } from 'src/modules/onboarding/components/BackButton';
import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import { makePersonalInfoValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { ContactConsentPolicyField } from 'src/modules/signUp/components/ContactConsentPolicyField';
import { TermsOfServiceField } from 'src/modules/signUp/components/TermsOfServiceField';
import {
  Form as SharedForm,
  TextField as SharedTextField,
} from 'src/shared/components/forms';
import {
  ConsentPolicyKinds,
  useCreateInsuranceUserMutation,
  useCreateProUserMutation,
  useGetConsentPoliciesQuery,
} from 'src/shared/graphql/generated';
import type {
  ConsentPolicy,
  OnboardingCreateUserMutation,
} from 'src/shared/graphql/generated';
import { useRefreshSession } from 'src/shared/graphql/hooks';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useSendCustomHubSpotEvent } from 'src/shared/hooks/hubspot';
import { Route as Routes } from 'src/shared/route';
import { Page, accountCreated, buttonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';
import { getUtmCookies } from 'src/shared/utils/cookie';

type PersonalInfoFormData = InferType<
  ReturnType<typeof makePersonalInfoValidationSchema>
>;

const Fields = ({
  isLoading,
  consentPolicy,
}: {
  isLoading: boolean;
  consentPolicy: ConsentPolicy;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const goBack = () => history.goBack();
  const { breakpoints } = useTheme();

  return (
    <>
      <Stack
        alignItems="start"
        direction={{ base: 'column', tablet: 'row' }}
        flexGrow={1}
        gap={{ base: 0, tablet: 500 }}
        justifyContent="space-between"
        marginTop={300}
        marginX="auto"
        maxWidth={breakpoints.width500}
        spacing={0}
        width={1}
      >
        <Heading
          marginBottom="16px !important"
          maxW={800}
          size={{ base: 600, tablet: 800 }}
        >
          {t('onboarding.yourPersonalInformation')}
        </Heading>
        <Box
          flexDirection="column"
          flexGrow={1}
          maxW={800}
          width={{ base: 1, tablet: '500px' }}
        >
          <SharedTextField
            id="firstName"
            label={t('onboarding.firstName')}
            name="firstName"
            placeholder=""
            spellCheck={false}
            type="text"
          />
          <SharedTextField
            id="lastName"
            label={t('onboarding.lastName')}
            name="lastName"
            placeholder=""
            spellCheck={false}
            type="text"
          />
          <SharedTextField
            id="companyName"
            label={t('onboarding.companyName')}
            name="companyName"
            placeholder=""
            spellCheck={false}
            type="text"
          />
          <SharedTextField
            id="phoneNumber"
            label={t('onboarding.phoneNumber')}
            name="phoneNumber"
            placeholder=""
            type="tel"
          />
          <Spacer />
          <TermsOfServiceField />
          {consentPolicy && (
            <ContactConsentPolicyField policy={consentPolicy} />
          )}
          <Box mb={400} mt={300}>
            <Button
              isLoading={isLoading}
              onClick={() => {
                buttonPressed({
                  button_text: 'Next',
                  page_or_screen_name: 'Onboarding-signup-email_input',
                  feature: 'onboarding_reskin',
                });
              }}
              size="large"
              type="submit"
              width={1}
            >
              {t('onboarding.finish')}
            </Button>
          </Box>
        </Box>
      </Stack>
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        flexDirection="column"
        width={1}
      >
        <Box
          justifyContent="start"
          maxW={breakpoints.width500}
          paddingBottom={300}
          width={1}
        >
          <BackButton onClick={goBack} />
        </Box>
      </Box>
    </>
  );
};

export const CreateAccountPro = () => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const refreshSession = useRefreshSession();

  const validationSchema = makePersonalInfoValidationSchema(t);
  const initialValues = {
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    hasAgreedToTerms: false,
    contactPolicy: undefined,
  };

  const toast = useToast();

  const history = useHistory();
  const { search } = useLocation();
  const sendHubSpotEvent = useSendCustomHubSpotEvent();
  const [state] = useContext(OnboardingContext);

  const { data: getConsentPolicies } = useGetConsentPoliciesQuery({
    variables: { kinds: [ConsentPolicyKinds.Sms] },
  });

  const consentPolicy = getConsentPolicies?.consentPolicies?.[0];

  const [createProUser, { loading: proLoading }] = useCreateProUserMutation();
  const [createInsuranceUser, { loading: insuranceLoading }] =
    useCreateInsuranceUserMutation();

  const onSubmit = async (formData: PersonalInfoFormData) => {
    const {
      firstName,
      lastName,
      companyName,
      phoneNumber,
      contactPolicy: { hasConsent: marketingConsent } = {},
    } = formData;
    const { email, password, orgType } = state;

    if (!password || !email || !orgType) return;

    let consents: [ConsentArgs] | undefined;

    if (consentPolicy?.id) {
      consents = [
        {
          policyId: consentPolicy.id,
          hasConsent: marketingConsent || false,
          source: 'web-signup',
        },
      ];
    }

    const utmCookies = getUtmCookies();
    let marketingCampaign;
    if (utmCookies.utmCampaign) {
      marketingCampaign = {
        name: utmCookies.utmCampaign,
        source: utmCookies.utmSource,
        medium: utmCookies.utmMedium,
        content: utmCookies.utmContent,
      };
    }

    const createUserVariables = {
      user: {
        firstName,
        lastName,
        email,
        password,
        passwordConfirmation: password,
        mobilePhone: phoneNumber,
        consents,
        marketingCampaign,
      },
      org: {
        name: companyName,
      },
    };

    buttonPressed({
      button_text: 'Accept and continue',
      page_or_screen_name: 'Email_input_password',
      feature: 'onboarding_reskin',
    });

    const onCompleted = ({
      onboardingCreateUser,
    }: OnboardingCreateUserMutation) =>
      accountCreated({
        button_or_link: 'button',
        button_or_link_text: t('onboarding.acceptAndContinue'),
        page_or_screen_name: Page.SIGN_UP_ONBOARDING,
        primary_cta: true,
        platform_type: 'web',
        user_id: onboardingCreateUser?.user?.id,
      });

    try {
      if (orgType === 'pro') {
        await createProUser({
          variables: createUserVariables,
          onCompleted,
        });
      }
      if (orgType === 'insurance') {
        await createInsuranceUser({
          variables: createUserVariables,
          onCompleted,
        });
      }

      const surveyPath = `${Routes.SIGN_UP_ONBOARDING.path}/description${search}`;

      void sendHubSpotEvent({
        email,
        eventName: 'pe23226235_sign-up-event',
      });

      await refreshSession();

      history.push(surveyPath);
    } catch (e) {
      const error = e as ApolloError;
      const graphQLErrors = error.graphQLErrors as GraphQLError[];
      if (graphQLErrors) {
        graphQLErrors.forEach(gqlError => {
          toast({
            description: gqlError.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            containerStyle: {
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
            },
          });
        });
      }
      logError(e as Error);
    }
  };

  if (!consentPolicy) return null;

  return (
    <Box alignItems="stretch" flex={1} width={1}>
      <SharedForm<PersonalInfoFormData>
        display="flex"
        flexGrow={1}
        initialValues={initialValues}
        marginX={500}
        marginY={{ base: 0, tablet: 400 }}
        onSubmit={onSubmit}
        transparentFieldset
        validationSchema={validationSchema}
      >
        <Fields
          consentPolicy={consentPolicy}
          isLoading={proLoading || insuranceLoading}
        />
      </SharedForm>
    </Box>
  );
};
